import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
// Import modules from swiper
import { Navigation, Pagination, Autoplay, Zoom, Mousewheel } from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faTimes } from '@fortawesome/free-solid-svg-icons';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { language, updateDocumentTitle } = useLanguage();
  const result = useData();
  const [activeTab, setActiveTab] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const isRTL = language.language === 'ar';
  const { site_info, navigation, contact_info, social_media } = result.data;

  // Add helper function to find service by slug
  const findServiceBySlug = (services, targetSlug) => {
    if (!services) return null;
    return Object.values(services).find(service => service.slug === targetSlug);
  };

  // Update service access
  const service = findServiceBySlug(result.data?.services, slug) || {};
  const gallery_images = service?.gallery_images || [];

  useEffect(() => {
    //console.log("Found service:", service);
  }, [service]);

  const ImageModal = ({ image, onClose }) => (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-90">
      <div className="relative px-4 w-full max-w-6xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 text-white transition-colors hover:text-gray-300"
        >
          <FontAwesomeIcon icon={faTimes} className="text-2xl" />
        </button>
        <Swiper
          modules={[Navigation, Pagination, Autoplay, Zoom, Mousewheel]}
          dir={isRTL ? 'rtl' : 'ltr'}
          spaceBetween={0}
          slidesPerView={1}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          pagination={{
            clickable: true,
            el: '.swiper-pagination'
          }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}

          keyboard={true}
                        mousewheel={{
                enabled: true,
                forceToAxis: true,
                releaseOnEdges: true,
                sensitivity: 2,
              }}
          loop={true}
          initialSlide={currentImage}
          className="modal-swiper"
        >
          {gallery_images.map((img, index) => (
            <SwiperSlide key={index}>
              <div className="flex items-center justify-center h-[80vh]">
                <img
                  src={img}
                  alt=""
                  className="object-contain max-w-full max-h-full"
                />
              </div>
            </SwiperSlide>
          ))}
          <div className="swiper-button-prev !text-white !text-xl !leading-8"></div>
          <div className="swiper-button-next !text-white !text-xl !leading-8"></div>
          <div className="swiper-pagination !bottom-6"></div>
        </Swiper>
      </div>
    </div>
  );

  // Update loading check to use slug
  if (!service?.slug) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-12 h-12 rounded-full border-t-2 border-b-2 animate-spin border-stone-800"></div>
      </div>
    );
  }

  useEffect(() => {
    if (result.data.site_info) {
      updateDocumentTitle(service.title, result.data?.site_info);
    }
  }, [result, t, language, updateDocumentTitle]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[70vh]">
        <img src={service.image} alt={service.title} className="object-cover w-full h-full" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70">
          <div className="container flex flex-col justify-end px-4 pb-16 mx-auto h-full">
            <h1 className="mb-4 text-5xl font-bold text-white">{service.title}</h1>
            <p className="max-w-2xl text-xl text-white/90">{service.description}</p>
          </div>
        </div>
      </section>

      <div className="container px-4 py-16 mx-auto">
        {/* Sub-services Section */}
        <div className={`grid md:grid-cols-12 gap-8 mb-16 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
          <div className="md:col-span-4">
            <div className="sticky top-24 p-4 bg-white rounded-xl shadow-sm">
              <h2 className="mb-4 text-xl font-semibold">{t('services.our_services')}</h2>
              <div className="space-y-2">
                {service.sub_services?.map((sub, index) => (
                  <button
                    key={index}
                    className={`w-full text-start p-4 rounded-lg transition-all ${activeTab === index
                      ? 'bg-stone-800 text-white shadow-lg'
                      : 'hover:bg-gray-100'
                      }`}
                    onClick={() => setActiveTab(index)}
                  >
                    {sub.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-8">
            <div className="p-6 bg-white rounded-xl shadow-sm">
              <h3 className="pb-4 mb-6 text-2xl font-semibold border-b">
                {service.sub_services?.[activeTab]?.title}
              </h3>
              <div className="flex flex-col gap-6 items-start md:flex-row">
                <div className="md:w-2/5 shrink-0">
                  <div className="overflow-hidden rounded-xl shadow-md aspect-[4/3] bg-gray-100">
                    <img
                      src={service.sub_services?.[activeTab]?.image}
                      alt=""
                      className="object-cover w-full h-full transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                </div>
                <div className="md:w-3/5">
                  <p className="max-w-none leading-relaxed text-gray-600 prose">
                    {service.sub_services?.[activeTab]?.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        {gallery_images.length > 0 && (
          <div className="mb-16">
            <h2 className="mb-8 text-3xl font-semibold">{t('services.gallery')}</h2>
            <Swiper
              modules={[Navigation, Pagination, Autoplay, Zoom, Mousewheel]}
              dir={isRTL ? 'rtl' : 'ltr'}
              spaceBetween={20}
              slidesPerView={1}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              pagination={{
                clickable: true,
                el: '.swiper-pagination'
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}

              keyboard={true}
                            mousewheel={{
                enabled: true,
                forceToAxis: true,
                releaseOnEdges: true,
                sensitivity: 2,
              }}
              loop={true}
              className="gallery-swiper"
            >
              {gallery_images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="aspect-[4/3] cursor-pointer rounded-xl overflow-hidden"
                    onClick={() => {
                      setCurrentImage(index);
                      setShowModal(true);
                    }}
                  >
                    <img src={image} alt="" className="object-cover w-full h-full transition-transform hover:scale-105" />
                  </div>
                </SwiperSlide>
              ))}
              <div className="swiper-button-prev !text-white !text-xl !leading-8"></div>
              <div className="swiper-button-next !text-white !text-xl !leading-8"></div>
              <div className="swiper-pagination !bottom-6"></div>
            </Swiper>
          </div>
        )}

        {/* Contact Button */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white rounded-lg shadow-lg transition-colors bg-stone-800 hover:bg-white hover:text-stone-900 hover:shadow-xl"
          >
            {t('footer.contact')}
            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </Link>
        </div>
      </div>

      {showModal && (
          <ImageModal
          image={gallery_images[currentImage]}
          onClose={() => setShowModal(false)}
          initialSlide={currentImage}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default ServiceDetail;
